import { useState } from 'react'
import { BaseEdge, EdgeLabelRenderer, getSmoothStepPath, getStraightPath, useReactFlow } from '@xyflow/react'
import { DEFAULT_TRANSITION_S, resolveTransitionDuration } from '../../lib/transitionDuration.js'
import { nodeControlClass } from '../../lib/nodeControlClass.js'
import { SM_THEME as T } from './stateMachineTheme'
import { polylinePath, smoothSplinePath, bowedBezierPath, insertIndexForClick } from '../../lib/edgeWaypoints'
import { DEFAULT_SPLINE_CURVATURE, splineTensionForCurvature } from '../../lib/smSplineStyle'

/**
 * TransitionEdge — edge inside a State Machine canvas.
 *
 * data.onOpenRule(edgeId)            — open the rule graph editor
 * data.hasRule                       — whether a rule graph exists
 * data.splineStyle                   — 'step' | 'arched' | 'straight' (ENG-1061)
 * data.curvature                     — arched-spline bend tightness (ENG-1061
 *   follow-up); only affects the 'arched' style. Falls back to
 *   DEFAULT_SPLINE_CURVATURE so an edge with no value renders as before.
 * data.hovered / data.anyHovered     — hover-to-trace state (ENG-1061): the
 *   hovered transition + its rule badge stay bright while the rest fade back,
 *   so a single connection is readable even when 18 of them overlap. Colours
 *   are also desaturated overall (Florent's feedback: the old bright-green +
 *   glow on every edge was visual noise).
 * data.onHover(edgeId) / data.onHoverEnd() — let the HTML rule badge join the
 *   same hover as the SVG line (ReactFlow's onEdgeMouseEnter only covers the
 *   path element, not the label layer).
 * data.waypoints                     — ordered [{x,y}] (flow coords) the wire is
 *   routed THROUGH (ENG-1061, Unreal-reroute style): double-click the wire to
 *   drop one, drag to reshape, double-click a waypoint to remove it.
 * data.onWaypointsChange(edgeId, next) — persist an edited waypoint list.
 */
export function TransitionEdge({
  id,
  sourceX, sourceY,
  targetX, targetY,
  sourcePosition, targetPosition,
  selected, markerEnd,
  data,
}) {
  const rf = useReactFlow()
  // ENG-1061: user-selectable spline shape. Default 'step' reproduces the
  // original getSmoothStepPath rendering exactly.
  const splineStyle = data?.splineStyle ?? 'step'
  const curvature   = data?.curvature   ?? DEFAULT_SPLINE_CURVATURE
  const waypoints   = data?.waypoints ?? []

  let edgePath, labelX, labelY
  if (waypoints.length > 0) {
    // Routed through user waypoints: arched → smooth spline, otherwise straight
    // segments. (An orthogonal step route through arbitrary points isn't well
    // defined, so step falls back to straight segments once rerouted.)
    const pts = [{ x: sourceX, y: sourceY }, ...waypoints, { x: targetX, y: targetY }]
    edgePath = splineStyle === 'arched'
      ? smoothSplinePath(pts, splineTensionForCurvature(curvature))
      : polylinePath(pts)
    const mid = pts[Math.floor(pts.length / 2)]
    labelX = mid.x
    labelY = mid.y
  } else {
    [edgePath, labelX, labelY] =
        splineStyle === 'arched'
          ? bowedBezierPath({ sourceX, sourceY, sourcePosition, targetX, targetY, targetPosition, curvature })
      : splineStyle === 'straight'
          ? getStraightPath({ sourceX, sourceY, targetX, targetY })
      :   getSmoothStepPath({ sourceX, sourceY, sourcePosition, targetX, targetY, targetPosition, borderRadius: 12 })
  }

  const isActive    = data?.isActive    ?? false
  const hasRule     = data?.hasRule     ?? false
  const ruleResult  = data?.ruleResult  // true | false | null/undefined
  const isEntryEdge = data?.isEntryEdge ?? false
  const actionCount = data?.actionCount ?? 0   // ENG-1062: fired custom events
  const labelOffset = data?.labelOffset ?? 0
  const hovered     = data?.hovered     ?? false
  const anyHovered  = data?.anyHovered  ?? false
  const dimmed      = anyHovered && !hovered   // another edge owns the spotlight

  // Desaturated palette — the bulk of the edges sit back (thin, muted, semi-
  // transparent); only the active / selected / hovered edge steps forward.
  const stroke =
      dimmed    ? T.edgeDimmed
    : hovered   ? T.edgeHover
    : isActive  ? T.edgeActive
    : selected  ? T.edgeSelected
    :             T.edgeIdle
  const strokeWidth  = hovered ? 3 : (selected || isActive) ? 2.25 : 1.4
  const strokeOpacity = dimmed ? 0.1 : hovered ? 1 : isActive ? 0.9 : 0.55
  const glow = dimmed ? 'none' : hovered ? T.edgeGlowHover : isActive ? T.edgeGlowActive : 'none'

  // Rule result dot color: green=true, red=false, gray=no result yet
  const ruleDotColor = ruleResult === true  ? '#60e060'
                     : ruleResult === false ? '#e06060'
                     : '#404040'

  const handleOpenRule = (e) => {
    e.stopPropagation()
    data?.onOpenRule?.(id)
  }

  // ── Reroute waypoints (ENG-1061) ──────────────────────────────────────────
  // Double-click the wire to drop a waypoint at the click, ordered along the route.
  const addWaypointAt = (e) => {
    e.stopPropagation()
    if (!data?.onWaypointsChange) return
    const p = rf.screenToFlowPosition({ x: e.clientX, y: e.clientY })
    const pts = [{ x: sourceX, y: sourceY }, ...waypoints, { x: targetX, y: targetY }]
    const at = insertIndexForClick(pts, p)
    const next = waypoints.slice()
    next.splice(at, 0, { x: p.x, y: p.y })
    data.onWaypointsChange(id, next)
  }
  // Drag a waypoint — screen delta ÷ zoom = flow delta, from a snapshot at grab.
  const startDrag = (e, index) => {
    e.stopPropagation()
    e.preventDefault()
    if (!data?.onWaypointsChange) return
    const sx = e.clientX, sy = e.clientY
    const zoom = rf.getViewport().zoom || 1
    const orig = waypoints[index]
    const move = (ev) => {
      const next = waypoints.slice()
      next[index] = { x: orig.x + (ev.clientX - sx) / zoom, y: orig.y + (ev.clientY - sy) / zoom }
      data.onWaypointsChange(id, next)
    }
    const up = () => {
      window.removeEventListener('pointermove', move)
      window.removeEventListener('pointerup', up)
    }
    window.addEventListener('pointermove', move)
    window.addEventListener('pointerup', up)
  }
  const removeWaypoint = (e, index) => {
    e.stopPropagation()
    if (!data?.onWaypointsChange) return
    const next = waypoints.slice()
    next.splice(index, 1)
    data.onWaypointsChange(id, next)
  }

  // The rule badge tracks its edge: faded with the rest, lit when its line is
  // the one being traced (so you can tell which rule belongs to which edge).
  const labelOpacity = dimmed ? 0.12 : 1

  return (
    <>
      <BaseEdge
        id={id}
        path={edgePath}
        markerEnd={markerEnd}
        style={{
          stroke,
          strokeWidth,
          strokeOpacity,
          filter: glow,
          transition: 'stroke 0.12s, stroke-opacity 0.12s',
        }}
      />
      {/* Invisible fat hit-path: double-click to drop a reroute waypoint.
          Entry edges aren't reroutable. */}
      {!isEntryEdge && data?.onWaypointsChange && (
        <path
          d={edgePath}
          fill="none"
          stroke="transparent"
          strokeWidth={16}
          style={{ pointerEvents: 'stroke', cursor: 'cell' }}
          onDoubleClick={addWaypointAt}
        />
      )}
      <EdgeLabelRenderer>
        {/* Reroute waypoint handles — drag to route, double-click to remove.
            The outer box is a generous 24px hit target (an 11px dot is far too
            small to land on with a mouse); the visible knob sits centered. */}
        {!dimmed && waypoints.map((w, i) => (
          <div
            key={i}
            className="nodrag nopan"
            title="Drag to move this reroute point · double-click to remove"
            onPointerDown={(e) => startDrag(e, i)}
            onDoubleClick={(e) => removeWaypoint(e, i)}
            style={{
              position: 'absolute',
              transform: `translate(-50%, -50%) translate(${w.x}px, ${w.y}px)`,
              width: 24,
              height: 24,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'grab',
              pointerEvents: 'all',
              touchAction: 'none',
              zIndex: 25,
              opacity: labelOpacity,
            }}
          >
            <div style={{
              width: hovered ? 15 : 12,
              height: hovered ? 15 : 12,
              borderRadius: '50%',
              background: T.edgeActive,
              border: `2px solid ${T.edgeHover}`,
              boxShadow: '0 0 0 3px rgba(0,0,0,0.4)',   // halo so it reads on any wire
              pointerEvents: 'none',
              transition: 'width 0.1s, height 0.1s',
            }} />
          </div>
        ))}
        <div
          onMouseEnter={() => data?.onHover?.(id)}
          onMouseLeave={() => data?.onHoverEnd?.()}
          style={{
            position: 'absolute',
            transform: `translate(-50%, -50%) translate(${labelX}px, ${labelY + labelOffset}px)`,
            pointerEvents: 'all',
            display: 'flex',
            alignItems: 'center',
            gap: 4,
            opacity: labelOpacity,
            zIndex: hovered ? 20 : 1,
            transition: 'opacity 0.12s',
          }}
          className="nodrag nopan"
        >
          {/* Rule result dot */}
          {hasRule && (
            <div title={ruleResult === true ? 'Rule: TRUE' : ruleResult === false ? 'Rule: FALSE' : 'Rule: not evaluated'} style={{
              width: 8, height: 8,
              borderRadius: '50%',
              background: ruleDotColor,
              boxShadow: ruleResult === true ? '0 0 4px #60e060' : 'none',
              flexShrink: 0,
            }} />
          )}

          {/* Rule editor button — hidden for entry edges */}
          {!isEntryEdge && (
            <button
              onClick={handleOpenRule}
              title="Edit transition rule"
              style={{
                background: hasRule ? '#1b2730' : '#171b1f',
                border: `1px solid ${hovered ? T.edgeHover : hasRule ? '#3a7f95' : '#33414a'}`,
                color: hasRule ? (hovered ? '#bfeaf5' : '#7fbece') : '#5a636d',
                borderRadius: 4,
                padding: '2px 7px',
                fontSize: 9,
                fontFamily: 'var(--m-font)',
                cursor: 'pointer',
                whiteSpace: 'nowrap',
              }}
            >
              ⚡ rule
            </button>
          )}

          {/* Blend chip — this transition's crossfade, readable across the whole
              machine without opening anything, and one click to edit. Hidden on
              entry edges: Entry is not a crossfade, it activates the initial
              state outright (updateSMTransitions). */}
          {!isEntryEdge && (
            <BlendChip
              value={data?.transitionDuration}
              hovered={hovered}
              onChange={(s) => data?.onDurationChange?.(id, s)}
            />
          )}

          {/* Actions badge (ENG-1062) — this transition fires N custom events */}
          {actionCount > 0 && (
            <div title={`Fires ${actionCount} custom event${actionCount === 1 ? '' : 's'} when taken`} style={{
              background: '#2a1010',
              border: '1px solid #b04040',
              color: '#ff9090',
              borderRadius: 4,
              padding: '2px 6px',
              fontSize: 9,
              fontFamily: 'var(--m-font)',
              whiteSpace: 'nowrap',
              flexShrink: 0,
            }}>
              ⚡ {actionCount}
            </div>
          )}
        </div>
      </EdgeLabelRenderer>
    </>
  )
}

// ── Blend chip ────────────────────────────────────────────────────────────────
// A transition's crossfade, on the edge. Shows the value at rest and swaps to an
// input on click — an always-on input per edge would be 41 number fields on the
// shipped machine, which is the density ENG-1061's muted palette exists to fix.
//
// AUTHORED vs INHERITED is the distinction the chip has to carry: an unset
// transition reads the shared 0.3s, so showing a bare "0.3" on every edge would
// make "I set this" and "nobody set this" look identical. Unset renders muted,
// authored renders in the edge's own accent, and 0 renders as "cut" because
// "0s" reads as a missing value rather than a deliberate snap.
function BlendChip({ value, hovered, onChange }) {
  const [editing, setEditing] = useState(false)
  const authored = value !== null && value !== undefined && value !== ''
  const seconds  = resolveTransitionDuration({ transitionDuration: value })

  const commit = (raw) => {
    setEditing(false)
    // Empty clears back to inherited; anything else goes through the same
    // resolver the runtime uses, so the chip can never show a value the
    // crossfade would not actually use.
    onChange?.(raw === '' ? null : resolveTransitionDuration({ transitionDuration: raw }))
  }

  if (editing) {
    return (
      // className comes FIRST here deliberately. nodeDragOptOut's detector reads
      // a tag's attributes with a negated-char class that stops at the first
      // angle bracket, and an arrow function in an earlier handler supplies one
      // — so a class written after onClick is invisible to the check.
      <input
        className={nodeControlClass({ extra: 'nopan' })}
        type="number" min={0} max={10} step={0.05}
        autoFocus
        defaultValue={authored ? value : ''}
        placeholder={String(DEFAULT_TRANSITION_S)}
        onClick={e => e.stopPropagation()}
        onBlur={e => commit(e.target.value)}
        onKeyDown={e => {
          if (e.key === 'Enter') commit(e.target.value)
          if (e.key === 'Escape') setEditing(false)
          e.stopPropagation()
        }}
        style={{
          width: 52, background: '#0e1418', border: `1px solid ${T.edgeHover}`,
          color: '#bfeaf5', borderRadius: 4, padding: '1px 4px',
          fontSize: 9, fontFamily: 'var(--m-mono)', outline: 'none',
        }}
      />
    )
  }

  return (
    <button
      onClick={e => { e.stopPropagation(); setEditing(true) }}
      title={authored
        ? `Crossfade ${seconds}s — click to edit (clear to inherit ${DEFAULT_TRANSITION_S}s)`
        : `Crossfade ${DEFAULT_TRANSITION_S}s (inherited) — click to set`}
      style={{
        background: authored ? '#101c22' : '#171b1f',
        border: `1px solid ${hovered ? T.edgeHover : authored ? '#3a7f95' : '#33414a'}`,
        color: authored ? (hovered ? '#bfeaf5' : '#7fbece') : '#5a636d',
        borderRadius: 4, padding: '2px 6px',
        fontSize: 9, fontFamily: 'var(--m-font)',
        cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0,
      }}
    >
      {seconds === 0 ? 'cut' : `${seconds}s`}
    </button>
  )
}
